import {
  Activity,
  BarChart3,
  CreditCard as CreditCardIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { TabsContent } from "@/components/ui/tabs"
import {
  DashboardInfoTypeEnum,
  DashboardPaymentTypeEnum,
  DashboardSmaugTypeEnum,
} from "@/enums"
import { useDashboard, useDashboardPayment } from "@/hooks"

import { useSidebar } from "../ui/sidebar"
import { DashboardBrandRecurringChart } from "./charts/brand-recurring"
import { DashboardInstallmentAmountRecurringChart } from "./charts/installment-amount-recurring"
import { DashboardRecurrenceStatusChart } from "./charts/recurrence-status"
import { DashboardVolumeDayWeekRecurringChart } from "./charts/volume-day-week-recurring"
import { DateRangePicker } from "./date-range-picker"
import { FinancialHealth } from "./sections/financial-health"
import { HeroMetrics } from "./sections/hero-metrics"
import { OperationalPerformance } from "./sections/operational-performance"
type DashboardRecurringTabContentProps =
  SearchParamsType & {
    isActive: boolean
  }
export const DashboardRecurringTabContent: React.FC<
  DashboardRecurringTabContentProps
> = ({ searchParams, isActive }) => {
  const { t } = useTranslation()
  const { open } = useSidebar()

  const {
    data: futureReceivables,
    isFetching: isLoadingFutureReceivables,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.FUTURE_RELEASES_RECURRING,
    enabled: isActive,
  })
  const {
    data: totalTransactionsRecurring,
    isFetching: isLoadingTotalTransactions,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.TOTAL_TRANSACTIONS_RECURRING,
    enabled: isActive,
  })
  const {
    data: transactedAmountRecurring,
    isFetching: isLoadingTransactedAmount,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.TRANSACTED_AMOUNT_RECURRING,
    enabled: isActive,
  })
  const {
    data: averageTicketRecurring,
    isFetching: isLoadingAverageTicket,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.AVERAGE_TICKET_RECURRING,
    enabled: isActive,
  })
  const {
    data: valueFeesRecurring,
    isFetching: isLoadingValueFees,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.VALUE_FEES_RECURRING,
    enabled: isActive,
  })
  const {
    data: refusedInstallmentsRecurring,
    isFetching: isLoadingRefusedInstallments,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.REFUSED_INSTALLMENTS_RECURRING,
    enabled: isActive,
  })
  const {
    data: receivableInstallmentsRecurring,
    isFetching: isLoadingReceivableInstallments,
  } = useDashboard({
    ...searchParams,
    dashboardType:
      DashboardInfoTypeEnum.RECEIVABLE_INSTALLMENTS_RECURRING,
    enabled: isActive,
  })
  const { data: reasonsRefusalRecurring } =
    useDashboardPayment({
      ...searchParams,
      dashboardType:
        DashboardPaymentTypeEnum.REASONS_REFUSAL_RECURRING,
      enabled: isActive,
    })
  const { data: approvalIndexFirstInstallment } =
    useDashboardPayment({
      ...searchParams,
      dashboardType:
        DashboardPaymentTypeEnum.APPROVAL_INDEX_FIRST_INSTALLMENT,
      enabled: isActive,
    })
  const { data: approvalIndexOtherInstallments } =
    useDashboardPayment({
      ...searchParams,
      dashboardType:
        DashboardPaymentTypeEnum.APPROVAL_INDEX_OTHER_INSTALLMENTS,
      enabled: isActive,
    })
  const {
    data: approvalIndexGeneralRecurring,
    isFetching: isLoadingApprovalIndex,
  } = useDashboardPayment({
    ...searchParams,
    dashboardType:
      DashboardPaymentTypeEnum.APPROVAL_INDEX_GENERAL_RECURRING,
    enabled: isActive,
  })
  const futureReceivablesValue = futureReceivables?.value
    ? futureReceivables.value / 100
    : 0
  const totalTransactionsRecurringValue =
    totalTransactionsRecurring?.value
      ? totalTransactionsRecurring.value
      : 0
  const transactedAmountRecurringValue =
    transactedAmountRecurring?.value
      ? transactedAmountRecurring.value / 100
      : 0
  const averageTicketRecurringValue =
    averageTicketRecurring?.value
      ? averageTicketRecurring.value / 100
      : 0
  const valueFeesRecurringValue = valueFeesRecurring?.value
    ? valueFeesRecurring.value / 100
    : 0
  const refusedInstallmentsRecurringValue =
    refusedInstallmentsRecurring?.value
      ? refusedInstallmentsRecurring.value / 100
      : 0
  const receivableInstallmentsRecurringValue =
    receivableInstallmentsRecurring?.value
      ? receivableInstallmentsRecurring.value / 100
      : 0
  const approvalIndexFirstValue =
    approvalIndexFirstInstallment?.indicator
      ?.find((item) => item.key === "Aprovado")
      ?.value.replace("%", "")
      ? Number(
          approvalIndexFirstInstallment.indicator
            .find((item) => item.key === "Aprovado")
            ?.value.replace("%", "") || "0"
        )
      : 0
  const approvalIndexOtherValue =
    approvalIndexOtherInstallments?.indicator
      ?.find((item) => item.key === "Aprovado")
      ?.value.replace("%", "")
      ? Number(
          approvalIndexOtherInstallments.indicator
            .find((item) => item.key === "Aprovado")
            ?.value.replace("%", "") || "0"
        )
      : 0
  const approvalIndexGeneralValue =
    approvalIndexGeneralRecurring?.indicator
      ?.find((item) => item.key === "Aprovado")
      ?.value.replace("%", "")
      ? Number(
          approvalIndexGeneralRecurring.indicator
            .find((item) => item.key === "Aprovado")
            ?.value.replace("%", "") || "0"
        )
      : 0
  return (
    <TabsContent
      value="recurring"
      className="group mt-4 space-y-8"
      data-sidebar-open={open}
    >
      <div className="flex items-center justify-end">
        <DateRangePicker searchParams={searchParams} />
      </div>

      {/* Hero Metrics Section */}
      <HeroMetrics
        revenue={{
          current: transactedAmountRecurringValue,
          isLoading: isLoadingTransactedAmount,
        }}
        volume={{
          current: totalTransactionsRecurringValue,
          isLoading: isLoadingTotalTransactions,
        }}
        averageTicket={{
          current: averageTicketRecurringValue,
          isLoading: isLoadingAverageTicket,
        }}
        approvalRate={{
          current: approvalIndexGeneralValue,
          isLoading: isLoadingApprovalIndex,
        }}
      />

      <Separator />

      {/* Financial Health Section */}
      <FinancialHealth
        futureReceivables={{
          value: futureReceivablesValue,
          isLoading: isLoadingFutureReceivables,
        }}
        installmentsReceivable={{
          value: valueFeesRecurringValue,
          isLoading: isLoadingValueFees,
        }}
        installmentsPaid={{
          value: receivableInstallmentsRecurringValue,
          isLoading: isLoadingReceivableInstallments,
        }}
        installmentsRefused={{
          value: refusedInstallmentsRecurringValue,
          isLoading: isLoadingRefusedInstallments,
        }}
      />

      <Separator />

      {/* Operational Performance Section */}
      <OperationalPerformance
        approvalData={{
          general: approvalIndexGeneralValue,
          firstInstallment: approvalIndexFirstValue,
          otherInstallments: approvalIndexOtherValue,
        }}
        refusalReasons={
          reasonsRefusalRecurring?.indicator || []
        }
        approvalIndexGeneralType={
          DashboardPaymentTypeEnum.APPROVAL_INDEX_GENERAL_RECURRING
        }
        searchParams={searchParams as SearchParamsType}
        enabled={isActive}
      />

      <Separator />

      {/* Detailed Analytics Section */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">
            {t(
              "pages.dashboard.sections.detailedAnalytics.title",
              "Análise Detalhada"
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              "pages.dashboard.sections.detailedAnalytics.description",
              "Gráficos e métricas complementares"
            )}
          </p>
        </div>
        <section className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card className="border-l-4 border-l-cyan-500 transition-all duration-200 hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.installmentQuantity.title"
                )}
              </CardTitle>
              <BarChart3 className="h-5 w-5 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <DashboardInstallmentAmountRecurringChart
                searchParams={searchParams}
              />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-teal-500 transition-all duration-200 hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.volumeByWeekday.title"
                )}
              </CardTitle>
              <Activity className="h-5 w-5 text-teal-500" />
            </CardHeader>
            <CardContent>
              <DashboardVolumeDayWeekRecurringChart
                dashboardType={
                  DashboardSmaugTypeEnum.VOLUME_DAY_WEEK_RECURRING
                }
                searchParams={
                  searchParams as SearchParamsType
                }
              />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500 transition-all duration-200 hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.brandDistribution.title"
                )}
              </CardTitle>
              <CreditCardIcon className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <DashboardBrandRecurringChart
                dashboardType={
                  DashboardSmaugTypeEnum.MOST_USED_BRAND_RECURRING
                }
                searchParams={
                  searchParams as SearchParamsType
                }
              />
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-indigo-500 transition-all duration-200 hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base font-medium">
                {t(
                  "pages.dashboard.cards.recurrenceStatus.title"
                )}
              </CardTitle>
              <BarChart3 className="h-5 w-5 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <DashboardRecurrenceStatusChart
                dashboardType={
                  DashboardInfoTypeEnum.RECURRENCE_STATUS_RECURRING
                }
                searchParams={
                  searchParams as SearchParamsType
                }
              />
            </CardContent>
          </Card>
        </section>
      </div>
    </TabsContent>
  )
}
